import React, { useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, ExternalLink, Play, CheckCircle2 } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useLang } from "../i18n/LangContext";
import useUTM from "../hooks/useUTM";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEBINARS = [
  { id: "awwUyG8eSMs", title: "Вступ до Великої Британії 2026–2027", duration: "1:21:06" },
  { id: "VIxZOHwkgFE", title: "Вступ за Кордон: США, UK, Нідерланди -68%", duration: "1:53:55" },
  { id: "9c5CvH4SrQ4", title: "Вступ-2026: ТОП виші світу — повний вебінар", duration: "2:00:40" },
  { id: "PhPdUmhaRtA", title: "ТОП університети світу 2026", duration: "2:13:22" },
  { id: "BWRUcwEwVyY", title: "Як вступити до університету США 2026 + Стипендії $30,000", duration: "2:06:05" },
  { id: "ytVQwBCpTso", title: "Як вступити до університету в Нідерландах", duration: "2:04:28" },
  { id: "v80BirD6vkI", title: "Вступ до Європи 2026–2027 — повний вебінар", duration: "1:57:32" },
  { id: "AEq4SYTQDH0", title: "Як здати IELTS на 8.0: секрети та лайфхаки", duration: "2:13:22" },
  { id: "1-0aCgZTr54", title: "Безкоштовне навчання в Чехії 2026", duration: "37:25" },
  { id: "2Y1RQ2ouppw", title: "Навчання в Швейцарії — кулінарна академія", duration: "17:20" },
  { id: "dx5b_CEbx3Y", title: "Чому всі обирають Нідерланди для навчання", duration: "3:42" },
  { id: "K7_tFuMbyvQ", title: "ТОП-5 університетів для українців", duration: "5:45" }
];

const I18N = {
  ru: {
    kicker: "ВЕБИНАРЫ STUDYGRI",
    titlePast: "Записи прошедших вебинаров",
    allOnYoutube: "Все вебинары на YouTube",
    titleNext: "Не пропусти следующий вебинар",
    subNext: "Регистрируйся — пришлём детали на email когда объявим тему и дату",
    name: "Имя", email: "Email", phone: "Телефон",
    submit: "Записаться на вебинар",
    sent: "Спасибо за регистрацию! 🎓", bonusKicker: "🎁 Бонус 100 €", bonusTitle: "Подпишись на канал — получи 100 € на услуги STUDYGRI", bonusDesc: "Подпишись на наш Telegram или Viber канал, чтобы активировать бонус 100 € на любой пакет услуг STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Заполните имя и email",
    errSubmit: "Попробуйте ещё раз"
  },
  uk: {
    kicker: "ВЕБІНАРИ STUDYGRI",
    titlePast: "Записи минулих вебінарів",
    allOnYoutube: "Всі вебінари на YouTube",
    titleNext: "Не пропусти наступний вебінар",
    subNext: "Реєструйся — надішлемо деталі на email коли оголосимо тему та дату",
    name: "Ім\u2019я", email: "Email", phone: "Телефон",
    submit: "Записатися на вебінар",
    sent: "Дякуємо за реєстрацію! 🎓", bonusKicker: "🎁 Бонус 100 €", bonusTitle: "Підпишись на канал — отримай 100 € на послуги STUDYGRI", bonusDesc: "Підпишись на наш Telegram або Viber канал, щоб активувати бонус 100 € на будь-який пакет послуг STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Заповніть ім\u2019я та email",
    errSubmit: "Спробуйте ще раз"
  },
  en: {
    kicker: "STUDYGRI WEBINARS",
    titlePast: "Past webinar recordings",
    allOnYoutube: "All webinars on YouTube",
    titleNext: "Don\u2019t miss the next webinar",
    subNext: "Register — we\u2019ll email you details when we announce the topic and date",
    name: "Name", email: "Email", phone: "Phone",
    submit: "Sign up for webinar",
    sent: "Thanks for registering! 🎓", bonusKicker: "🎁 €100 Bonus", bonusTitle: "Subscribe to our channel and get €100 off STUDYGRI services", bonusDesc: "Subscribe to our Telegram or Viber channel to unlock a €100 bonus on any STUDYGRI service package.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Enter name and email",
    errSubmit: "Please try again"
  },
  ar: {
    kicker: "ندوات STUDYGRI",
    titlePast: "تسجيلات الندوات السابقة",
    allOnYoutube: "جميع الندوات على YouTube",
    titleNext: "لا تفوت الندوة القادمة",
    subNext: "سجّل — وسنرسل لك التفاصيل عبر البريد عند الإعلان عن الموضوع والتاريخ",
    name: "الاسم", email: "البريد الإلكتروني", phone: "الهاتف",
    submit: "التسجيل في الندوة",
    sent: "شكراً على التسجيل! 🎓", bonusKicker: "🎁 مكافأة 100 €", bonusTitle: "اشترك في قناتنا واحصل على خصم 100 € على خدمات STUDYGRI", bonusDesc: "اشترك في قناتنا على Telegram أو Viber للحصول على مكافأة 100 € على أي باقة خدمات STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "أدخل الاسم والبريد الإلكتروني",
    errSubmit: "حاول مرة أخرى"
  },
  it: {
    kicker: "WEBINAR STUDYGRI",
    titlePast: "Registrazioni dei webinar passati",
    allOnYoutube: "Tutti i webinar su YouTube",
    titleNext: "Non perdere il prossimo webinar",
    subNext: "Registrati — ti invieremo i dettagli via email quando annunceremo tema e data",
    name: "Nome", email: "Email", phone: "Telefono",
    submit: "Iscriviti al webinar",
    sent: "Grazie per la registrazione! 🎓", bonusKicker: "🎁 Bonus 100 €", bonusTitle: "Iscriviti al canale e ricevi 100 € sui servizi STUDYGRI", bonusDesc: "Iscriviti al nostro canale Telegram o Viber per attivare un bonus di 100 € su qualsiasi pacchetto STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Compila nome ed email",
    errSubmit: "Riprova"
  },
  zh: {
    kicker: "STUDYGRI 网络研讨会",
    titlePast: "往期研讨会录像",
    allOnYoutube: "在 YouTube 上观看全部",
    titleNext: "不要错过下一场研讨会",
    subNext: "立即注册 — 公布主题与日期后我们会通过邮箱通知您",
    name: "姓名", email: "邮箱", phone: "电话",
    submit: "报名网络研讨会",
    sent: "感谢您的注册！🎓", bonusKicker: "🎁 100€ 礼遇", bonusTitle: "订阅我们的频道，享受 100€ STUDYGRI 服务优惠", bonusDesc: "订阅我们的 Telegram 或 Viber 频道，即可激活任意 STUDYGRI 服务套餐 100€ 礼遇。", tgBtn: "Telegram", viberBtn: "Viber",
    err: "请填写姓名和邮箱",
    errSubmit: "请重试"
  },
  es: {
    kicker: "WEBINARS STUDYGRI",
    titlePast: "Grabaciones de webinars anteriores",
    allOnYoutube: "Todos los webinars en YouTube",
    titleNext: "No te pierdas el próximo webinar",
    subNext: "Regístrate — te enviaremos los detalles por email cuando anunciemos tema y fecha",
    name: "Nombre", email: "Email", phone: "Teléfono",
    submit: "Apuntarme al webinar",
    sent: "¡Gracias por registrarte! 🎓", bonusKicker: "🎁 Bono 100 €", bonusTitle: "Suscríbete al canal y obtén 100 € en servicios STUDYGRI", bonusDesc: "Suscríbete a nuestro canal de Telegram o Viber para activar un bono de 100 € en cualquier paquete STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Introduce nombre y email",
    errSubmit: "Inténtalo de nuevo"
  },
  pl: {
    kicker: "WEBINARY STUDYGRI",
    titlePast: "Nagrania poprzednich webinarów",
    allOnYoutube: "Wszystkie webinary na YouTube",
    titleNext: "Nie przegap kolejnego webinaru",
    subNext: "Zapisz się — wyślemy Ci szczegóły mailem, gdy ogłosimy temat i datę",
    name: "Imię", email: "Email", phone: "Telefon",
    submit: "Zapisz się na webinar",
    sent: "Dziękujemy za rejestrację! 🎓", bonusKicker: "🎁 Bonus 100 €", bonusTitle: "Zasubskrybuj kanał i odbierz 100 € na usługi STUDYGRI", bonusDesc: "Zasubskrybuj nasz kanał Telegram lub Viber, aby aktywować bonus 100 € na dowolny pakiet STUDYGRI.", tgBtn: "Telegram", viberBtn: "Viber",
    err: "Wpisz imię i email",
    errSubmit: "Spróbuj ponownie"
  }
};

const Webinars = () => {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.uk;
  const isRtl = lang === 'ar';
  const carouselRef = useRef(null);
  const utm = useUTM();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const scroll = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast({ title: t.err, variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name, phone, email,
        country: "", program: "Webinar",
        message: "Webinar signup",
        lang, source: "webinar-signup",
        ...utm
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'webinar_registration_success', form_source: 'webinar-sent' });
      setSent(true);
      toast({ title: t.sent });
    } catch {
      toast({ title: t.errSubmit, variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <section className="relative" style={{ background: '#1B2A4A', color: '#fff' }} dir={isRtl ? 'rtl' : 'ltr'} data-testid="webinars-section">
      {/* PART 1 — past webinars */}
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 pt-20 md:pt-24 pb-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: '#C9A96E' }}>{t.kicker}</div>
            <h2 className="font-display text-3xl md:text-5xl text-balance" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{t.titlePast}</h2>
          </div>
          <div className="flex gap-3 shrink-0">
            <button onClick={() => scroll(-1)} aria-label="prev" className="w-12 h-12 rounded-full flex items-center justify-center transition hover:opacity-80" style={{ background: 'transparent', border: '1.5px solid #C9A96E', color: '#C9A96E' }} data-testid="webinar-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => scroll(1)} aria-label="next" className="w-12 h-12 rounded-full flex items-center justify-center transition hover:opacity-80" style={{ background: 'transparent', border: '1.5px solid #C9A96E', color: '#C9A96E' }} data-testid="webinar-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={carouselRef}
          className="flex gap-5 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-2"
          style={{ scrollPaddingLeft: '8px' }}
          data-testid="webinar-carousel"
        >
          {WEBINARS.map((w) => (
            <a
              key={w.id}
              href={`https://youtu.be/${w.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="snap-start shrink-0 w-[270px] sm:w-[300px] rounded-2xl overflow-hidden relative group transition hover:-translate-y-1"
              style={{ border: '1px solid rgba(201,169,110,0.35)' }}
              data-testid={`webinar-card-${w.id}`}
            >
              <div className="relative aspect-video">
                <img
                  src={`https://img.youtube.com/vi/${w.id}/maxresdefault.jpg`}
                  alt={w.title}
                  onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${w.id}/hqdefault.jpg`; }}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Duration badge */}
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md text-[11px] font-bold" style={{ background: '#C9A96E', color: '#1B2A4A' }}>
                  {w.duration}
                </div>
                {/* Play icon */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition" style={{ background: 'rgba(27,42,74,0.45)' }}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#C9A96E' }}>
                    <Play className="w-6 h-6 ml-1" style={{ color: '#1B2A4A' }} strokeWidth={2.5} fill="#1B2A4A" />
                  </div>
                </div>
                {/* Bottom gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-2/3 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(27,42,74,0.98), transparent)' }} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-3">
                <div className="font-display text-[15px] leading-snug text-white line-clamp-2" style={{ fontWeight: 700 }}>{w.title}</div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="https://www.youtube.com/@studygri"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold transition hover:opacity-90"
            style={{ background: 'transparent', border: '1.5px solid #C9A96E', color: '#C9A96E' }}
            data-testid="webinar-all-youtube"
          >
            {t.allOnYoutube} <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Separator */}
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,169,110,0.5), transparent)' }} />
      </div>

      {/* PART 2 — next webinar signup */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20 text-center">
        <h3 className="font-display text-2xl md:text-4xl text-balance" style={{ fontWeight: 900, letterSpacing: '-0.02em' }}>{t.titleNext}</h3>
        <p className="mt-4 text-sm md:text-base max-w-xl mx-auto" style={{ color: '#C9A96E' }}>{t.subNext}</p>

        {!sent ? (
          <form onSubmit={submit} className="mt-9 flex flex-col md:flex-row gap-3 max-w-3xl mx-auto" data-testid="webinar-form">
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder={`${t.name} *`}
              className="flex-1 h-12 px-5 rounded-full text-sm outline-none transition focus:ring-2"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff' }}
              data-testid="webinar-name"
            />
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={`${t.email} *`}
              className="flex-1 h-12 px-5 rounded-full text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff' }}
              data-testid="webinar-email"
            />
            <input
              type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder={t.phone}
              className="flex-1 h-12 px-5 rounded-full text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff' }}
              data-testid="webinar-phone"
            />
            <button
              type="submit" disabled={busy}
              className="h-12 px-7 rounded-full text-sm font-bold transition hover:opacity-90 whitespace-nowrap"
              style={{ background: '#C9A96E', color: '#1B2A4A' }}
              data-testid="webinar-submit"
            >
              {busy ? '...' : `${t.submit} →`}
            </button>
          </form>
        ) : (
          <div className="mt-9 max-w-2xl mx-auto rounded-2xl p-7 md:p-8 text-left" style={{ background: 'linear-gradient(135deg, rgba(201,169,110,0.14), rgba(201,169,110,0.04))', border: '1px solid rgba(201,169,110,0.45)' }} data-testid="webinar-sent">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-7 h-7 shrink-0" style={{ color: '#C9A96E' }} />
              <span className="text-base md:text-lg font-semibold" style={{ color: '#1B2A4A' }}>{t.sent}</span>
            </div>
            <div className="rounded-xl p-5 mb-5" style={{ background: 'rgba(27,42,74,0.04)' }}>
              <div className="text-xs font-bold tracking-[0.18em] uppercase mb-2" style={{ color: '#C9A96E' }}>{t.bonusKicker}</div>
              <div className="font-display text-2xl md:text-3xl leading-tight mb-2" style={{ color: '#1B2A4A', fontFamily: "'Forum','Cormorant Garamond',serif" }}>{t.bonusTitle}</div>
              <p className="text-sm md:text-base" style={{ color: '#5a6a6a' }}>{t.bonusDesc}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="https://t.me/studygriweb" target="_blank" rel="noopener noreferrer" data-testid="webinar-tg-btn" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.10em] transition-all hover:scale-[1.02]" style={{ background: '#229ED9', color: '#fff', boxShadow: '0 8px 20px -8px rgba(34,158,217,0.55)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
                {t.tgBtn}
              </a>
              <a href="https://bit.ly/viber-ua" target="_blank" rel="noopener noreferrer" data-testid="webinar-viber-btn" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-bold uppercase tracking-[0.10em] transition-all hover:scale-[1.02]" style={{ background: '#7360F2', color: '#fff', boxShadow: '0 8px 20px -8px rgba(115,96,242,0.55)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.4.04C9.5.05 5.46.32 3.2 2.4 1.52 4.08.93 6.55.86 9.61c-.07 3.05-.15 8.78 5.36 10.33h.01v2.37s-.03.96.6 1.15c.76.24 1.2-.49 1.93-1.28.4-.43.95-1.07 1.37-1.55 3.77.32 6.67-.4 7-.51.76-.25 5.06-.8 5.76-6.51.72-5.89-.35-9.6-2.27-11.28h-.01C19.62.78 16.96.04 14.96.04l-3.56 0z"/></svg>
                {t.viberBtn}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Webinars;
